/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable id-denylist, max-lines-per-function, max-lines */
import { Products } from '@zencity/customer-management-client';
import {
  AvailableProductsType,
  DataItemTypes,
  IDataItemDefinition,
  IWidgetBundleSchema,
} from '@zencity/reporting-types';
import { ClassificationTop3ConcernsBreakdownHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ClassificationTop3ConcernsBreakdownHOC/ClassificationTop3ConcernsBreakdownHOC';
import { ClassificationTopConcernsByAreaHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ClassificationTopConcernsByAreaHOC/ClassificationTopConcernsByAreaHOC';
import { ClassificationTopConcernsHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ClassificationTopConcernsHOC/ClassificationTopConcernsHOC';
import { DemographicsTableHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/DemographicsTableHOC';
import { OverallSatisfactionCharttHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/OverallSatisfactionChartHOC';
import { OverallSatisfactionTextHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/OverallSatisfactionTextHOC';
import { ScoresOverTimeByArea } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ScoresOverTimeByArea/ScoresOverTimeByArea';
import { ServicesBarChartHOC } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ServicesBarChartHOC';
import { withWidgetGeneratorContextForWidgets } from 'screens/WidgetGenerator/reportGeneratorComponents/components/WidgetWrappers/WidgetWrappers';
import { choicesQuestionChartDefinition } from 'screens/WidgetGenerator/reportGeneratorComponents/dataItemDefinitions/definitions/choicesQuestionChart';
import { questionnaireDefinition } from 'screens/WidgetGenerator/reportGeneratorComponents/dataItemDefinitions/definitions/questionnaire';
import { wordCloudDefinition } from 'screens/WidgetGenerator/reportGeneratorComponents/dataItemDefinitions/definitions/wordCloud';
import {
  SurveysReportGeneratorBundleKey,
  SurveysReportGeneratorDataItemKey,
  SurveysReportGeneratorFilterKey,
} from 'screens/WidgetGenerator/reportGeneratorComponents/enums';
import { productPrefixDefinitionKey } from 'screens/WidgetGenerator/reportGeneratorComponents/helpers/dataItemDefinition';
import { ReportBuilderClientData } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import '../../../i18n';
import { AreasSentimentBarTableHOC } from './components/AreasSentimentTableHOC';
import { BenchmarkGenericQuestionsTableHOC } from './components/BenchmarkGenericQuestionsTableHOC/BenchmarkGenericQuestionsTableHOC';
import { TableLegend } from './components/BenchmarkGenericQuestionsTableHOC/TableLegend';
import { BenchmarkOverallSatisfactionTextHOC } from './components/BenchmarkOverallSatisfactionTextHOC';
import { CrossTabsHOC } from './components/CrossTabsHOC/CrossTabsHOC';
import { MaintainAndFocusServicesHOC } from './components/MaintainAndFocusServicesHOC/MaintainAndFocusServicesHOC';
import { OverallSentimentTableHOC } from './components/OverallSentimentTableHOC';
import { ScoreTextWithSentimentBarHOC } from './components/ServiceSentimentBarHOC';
import { TopicsBubbleChartHOC } from './components/TopicsBubbleChartHOC/TopicsBubbleChartHOC';
import { SUPER_ADMIN_WIDGETS, widgetLabels } from './dataItemDefinitions/helpers';

// TODO: After speaking with Amir O., there is an error in the types definition for the `DataItemDefinition` interface.
// The `component` field is not required for a `bundle` type, but it is required for a `single-widget` type.
// As such, this interface is copied from Core-Platform, which uses its own types definition for the Data Item Definitions
// that once was the source of truth. This should be updated in the future to use the types definition from @zencity/reporting-types.
export interface WidgetBundleDefinition {
  key: string;
  type: DataItemTypes.WIDGET_BUNDLE;
  product: AvailableProductsType;
  name: string;
  filters: string[];
  description?: string;
  schema: IWidgetBundleSchema[];
}

const filterOutSuperAdminWidgets = (widgets: IDataItemDefinition[]): IDataItemDefinition[] =>
  widgets.filter((widget) => !SUPER_ADMIN_WIDGETS.includes(widget.key as SurveysReportGeneratorDataItemKey));

export const buildBundleDataItemDefinitions = (isSuperAdmin: boolean): WidgetBundleDefinition[] => {
  const surveysBundles: WidgetBundleDefinition[] = [
    {
      key: SurveysReportGeneratorBundleKey.OVERALL_BUNDLE,
      type: DataItemTypes.WIDGET_BUNDLE,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorBundleKey.OVERALL_BUNDLE],
      description: 'Overall Satisfaction Text & Chart for the Cycle',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      schema: [
        {
          name: 'heading2',
          children: [
            {
              data: 'Overall Satisfaction Score',
            },
          ],
        },
        {
          name: 'twoColumns',
          children: [
            {
              name: 'reactItem',
              attributes: {
                definitionKey: productPrefixDefinitionKey(
                  SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_TEXT,
                  Products.COMMUNITY_SURVEY,
                ),
              },
            },
            {
              name: 'reactItem',
              attributes: {
                definitionKey: productPrefixDefinitionKey(
                  SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_CHART,
                  Products.COMMUNITY_SURVEY,
                ),
              },
            },
          ],
        },
      ],
    },
  ];
  if (!isSuperAdmin) {
    return surveysBundles.filter(
      (bundle) => !SUPER_ADMIN_WIDGETS.includes(bundle.key as SurveysReportGeneratorBundleKey),
    );
  }
  return surveysBundles;
};

const addGeneralFiltersToDataItem = (item: IDataItemDefinition): IDataItemDefinition => {
  if ('filters' in item) {
    item.filters.push(SurveysReportGeneratorFilterKey.SURVEY_GROUP);
  }
  return item;
};

export const buildSingleWidgetDataItemDefinitions = (
  isSuperAdmin: boolean,
  clientData: ReportBuilderClientData,
): IDataItemDefinition[] => {
  const surveysWidgets: IDataItemDefinition[] = [
    {
      key: SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS_BY_AREA,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.BLOCKWISE,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS_BY_AREA],
      description: 'Top concerns by area',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.CLASSIFIED_OPEN_ENDED_GENERIC_QUESTION,
        SurveysReportGeneratorFilterKey.DIVISION,
        SurveysReportGeneratorFilterKey.OVERRIDE_COLORS_MAPPING,
      ],
      component: withWidgetGeneratorContextForWidgets(
        ClassificationTopConcernsByAreaHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS_BY_AREA],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_3_CONCERNS_BREAKDOWN,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.BLOCKWISE,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_3_CONCERNS_BREAKDOWN],
      description: 'Top 3 concerns breakdown',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.CLASSIFIED_OPEN_ENDED_GENERIC_QUESTION,
        SurveysReportGeneratorFilterKey.DIVISION,
      ],
      component: withWidgetGeneratorContextForWidgets(
        ClassificationTop3ConcernsBreakdownHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_3_CONCERNS_BREAKDOWN],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.BLOCKWISE,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS],
      description: 'Top concerns',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.CLASSIFIED_OPEN_ENDED_GENERIC_QUESTION,
        SurveysReportGeneratorFilterKey.DIVISION,
      ],
      component: withWidgetGeneratorContextForWidgets(
        ClassificationTopConcernsHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.CLASSIFICATION_TOP_CONCERNS],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_TEXT,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_TEXT],
      description: 'Overall Satisfaction Text',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        OverallSatisfactionTextHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_TEXT],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_CHART,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_CHART],
      description: 'Overall Satisfaction Chart',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        OverallSatisfactionCharttHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.OVERALL_SATISFACTION_CHART],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.SENTIMENT_BAR_OVERALL_TABLE,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.SENTIMENT_BAR_OVERALL_TABLE],
      description: 'Sentiment Bars for Overall Score',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        OverallSentimentTableHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.SENTIMENT_BAR_OVERALL_TABLE],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.AREAS_SENTIMENT_TABLE,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.AREAS_SENTIMENT_TABLE],
      description: 'Residents satisfaction - areas breakdown',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        AreasSentimentBarTableHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.AREAS_SENTIMENT_TABLE],
        clientData,
      ),
    },
    ...questionnaireDefinition({
      products: [Products.COMMUNITY_SURVEY, Products.BLOCKWISE, Products.PULSE],
      clientData,
    }),
    {
      key: SurveysReportGeneratorDataItemKey.TOPICS_BUBBLE_CHART,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.TOPICS_BUBBLE_CHART],
      description: 'Topics Bubble Chart',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SCALE_SELECT,
        SurveysReportGeneratorFilterKey.TOPICS_BUBBLE_CHART_SENTIMENT_SELECT,
      ],
      component: withWidgetGeneratorContextForWidgets(
        TopicsBubbleChartHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.TOPICS_BUBBLE_CHART],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.SERVICES_BAR_CHART,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.SERVICES_BAR_CHART],
      description: 'Stacked Bar Chart',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_NUM_BARS_PER_ROW,
        SurveysReportGeneratorFilterKey.SELECTED_SERVICE_BAR_ASPECTS,
        SurveysReportGeneratorFilterKey.SERVICES_BAR_CHART_TABLE_TO_DISPLAY,
      ],
      component: withWidgetGeneratorContextForWidgets(
        ServicesBarChartHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.SERVICES_BAR_CHART],
        clientData,
      ),
    },
    ...wordCloudDefinition({
      products: [Products.COMMUNITY_SURVEY, Products.BLOCKWISE, Products.PULSE],
      clientData,
    }),
    {
      key: SurveysReportGeneratorDataItemKey.DEMOGRAPHICS_TABLE,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.DEMOGRAPHICS_TABLE],
      description: 'Demographics Table',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.DEMOGRAPHICS_TABLE_ROWS_PER_TABLE,
      ],
      component: withWidgetGeneratorContextForWidgets(
        DemographicsTableHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.DEMOGRAPHICS_TABLE],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.MAINTAIN_AND_FOCUS_BAR_CHARTS,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.MAINTAIN_AND_FOCUS_BAR_CHARTS],
      description: 'Maintain and Focus Services',
      filters: [SurveysReportGeneratorFilterKey.CYCLES, SurveysReportGeneratorFilterKey.MAINTAIN_AND_FOCUS_FILTERS],
      component: withWidgetGeneratorContextForWidgets(
        MaintainAndFocusServicesHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.MAINTAIN_AND_FOCUS_BAR_CHARTS],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.CROSS_TABS,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.CROSS_TABS],
      description: 'Cross Tabs',
      filters: [SurveysReportGeneratorFilterKey.CYCLES, SurveysReportGeneratorFilterKey.CROSS_TABS_FILTERS],
      component: withWidgetGeneratorContextForWidgets(
        CrossTabsHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.CROSS_TABS],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.SERVICE_SENTIMENT_BAR,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.SERVICE_SENTIMENT_BAR],
      description: 'Cross Tabs',
      filters: [
        SurveysReportGeneratorFilterKey.CYCLES,
        SurveysReportGeneratorFilterKey.SERVICE_SENTIMENT_BAR_QUESTION_SELECT,
      ],
      component: withWidgetGeneratorContextForWidgets(
        ScoreTextWithSentimentBarHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.SERVICE_SENTIMENT_BAR],
        clientData,
      ),
    },
    ...choicesQuestionChartDefinition({
      products: [Products.COMMUNITY_SURVEY, Products.BLOCKWISE, Products.PULSE],
      clientData,
    }),
    {
      key: SurveysReportGeneratorDataItemKey.BENCHMARK_OVERALL_SATISFACTION,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.BENCHMARK_OVERALL_SATISFACTION],
      description: 'Overall Benchmark Scores',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        BenchmarkOverallSatisfactionTextHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.BENCHMARK_OVERALL_SATISFACTION],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.QUESTIONS_BENCHMARK_TABLE,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.QUESTIONS_BENCHMARK_TABLE],
      description: 'Questions Benchmark table',
      filters: [SurveysReportGeneratorFilterKey.CYCLES, SurveysReportGeneratorFilterKey.BENCHMARK_ASPECT_SELECT],
      component: withWidgetGeneratorContextForWidgets(
        BenchmarkGenericQuestionsTableHOC,
        widgetLabels[SurveysReportGeneratorDataItemKey.QUESTIONS_BENCHMARK_TABLE],
        clientData,
      ),
    },
    {
      key: SurveysReportGeneratorDataItemKey.BENCHMARK_TABLE_LEGEND,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.COMMUNITY_SURVEY,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.BENCHMARK_TABLE_LEGEND],
      description: 'Benchmark table legend',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      component: withWidgetGeneratorContextForWidgets(
        TableLegend,
        widgetLabels[SurveysReportGeneratorDataItemKey.BENCHMARK_TABLE_LEGEND],
        clientData,
      ),
    },
    // This is a Blockwise widget, so no need to use the widget generator context.
    {
      key: SurveysReportGeneratorDataItemKey.SCORES_OVER_TIME_BY_AREA,
      type: DataItemTypes.SINGLE_WIDGET,
      product: Products.BLOCKWISE,
      name: widgetLabels[SurveysReportGeneratorDataItemKey.SCORES_OVER_TIME_BY_AREA],
      description: 'Scores Over Time By Area',
      filters: [SurveysReportGeneratorFilterKey.CYCLES],
      allowInteraction: true,
      component: withWidgetGeneratorContextForWidgets(
        ScoresOverTimeByArea,
        widgetLabels[SurveysReportGeneratorDataItemKey.SCORES_OVER_TIME_BY_AREA],
        clientData,
      ),
    },
  ];
  const surveysWidgetsWithGeneralFilters = surveysWidgets.map(addGeneralFiltersToDataItem);

  const endUserWidgets = filterOutSuperAdminWidgets(surveysWidgetsWithGeneralFilters);
  if (!isSuperAdmin) {
    return endUserWidgets;
  }
  return surveysWidgetsWithGeneralFilters;
};
